import { Component, OnDestroy, OnInit } from '@angular/core';
import { AdminService } from '../../services/admin/admin.service';
import { HomeCourseContentModel } from 'src/app/core/HomeCourseContentModel';
import { TagModel } from 'src/app/core/models';

interface ShortCourseAdminCard {
  code: string;
  icon: string;
  iconClass: string;
  buttonClass: string;
  buttonText: string;
}

@Component({
  selector: 'app-admin-public-home-content',
  templateUrl: './admin-public-home-content.component.html',
  styleUrls: ['./admin-public-home-content.component.scss']
})
export class AdminPublicHomeContentComponent implements OnInit, OnDestroy {

  homeContent: HomeCourseContentModel[] = [];

  isLoading = false;
  isSaving = false;

  editingKey: string | null = null;
  editValue = '';

  message = '';
  messageType: 'success' | 'error' = 'success';

  /*
   * Match the PUBLIC new-ecb- card classes, while retaining the
   * original four course content codes and preview button labels.
   */
  shortCourseCards: ShortCourseAdminCard[] = [
    {
      code: 'ALL_COURSES',
      icon: 'bi bi-clipboard2-check',
      iconClass: 'new-ecb-short-uoc-icon',
      buttonClass: 'new-ecb-short-uoc-button',
      buttonText: 'View Courses'
    },
    {
      code: 'VOC',
      icon: 'bi bi-patch-check',
      iconClass: 'new-ecb-short-voc-icon',
      buttonClass: 'new-ecb-short-voc-button',
      buttonText: 'View Assessments'
    },
    {
      code: 'SKILL_SETS',
      icon: 'bi bi-journal-bookmark-fill',
      iconClass: 'new-ecb-short-skill-icon',
      buttonClass: 'new-ecb-short-skill-button',
      buttonText: 'View Courses'
    },
    {
      code: 'HIGH_RISK',
      icon: 'bi bi-cone-striped',
      iconClass: 'new-ecb-short-high-risk-icon',
      buttonClass: 'new-ecb-short-high-risk-button',
      buttonText: 'View Courses'
    }
  ];

  /* HSR uses contentCode = 'HSR' from the existing HomeCourseContent API.
     That backend record must exist for inline saving to be enabled. */
  readonly hsrContentCode = 'HSR';

  qualificationSlots: {
    position: string;
    icon: string;
    iconClass: string;
    tag: TagModel | null;
  }[] = [
    {
      position: 'LEFT_TOP',
      icon: 'bi bi-award',
      iconClass: 'ecb-web-certificate-two-icon',
      tag: null
    },
    {
      position: 'RIGHT_TOP',
      icon: 'bi bi-patch-check',
      iconClass: 'ecb-web-certificate-three-icon',
      tag: null
    },
    {
      position: 'BOTTOM_LEFT',
      icon: 'bi bi-journal-check',
      iconClass: 'ecb-web-certificate-four-icon',
      tag: null
    },
    {
      position: 'BOTTOM_RIGHT',
      icon: 'bi bi-file-earmark-medical',
      iconClass: 'ecb-web-diploma-icon',
      tag: null
    }
  ];

  savedFieldKey: string | null = null;
  private savedFieldTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private adminService: AdminService) { }

  ngOnInit(): void {
    this.loadHomeCourseContent();
    this.loadQualifications();
  }

  ngOnDestroy(): void {
    if (this.savedFieldTimer) {
      clearTimeout(this.savedFieldTimer);
    }
  }

  trackShortCourseCard(_index: number, card: ShortCourseAdminCard): string {
    return card.code;
  }

  loadQualifications(): void {
    this.adminService.getTags('PUBLIC_HOME_GRID').subscribe({
      next: (response: TagModel[]) => {
        const tags = (response || []).map(tag => ({
          ...tag,
          dashboard: tag.dashboard?.trim() || null,
          plainDescription: this.getPlainDescription(tag.description || '')
        }));

        this.qualificationSlots = this.qualificationSlots.map(slot => ({
          ...slot,
          tag: tags.find(tag => tag.dashboard === slot.position) || null
        }));
      },
      error: error => {
        console.error('Failed to load qualifications:', error);
      }
    });
  }

  getPlainDescription(html: string): string {
    if (!html) {
      return '';
    }

    const element = document.createElement('div');
    element.innerHTML = html;

    const plainText = (element.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const maxLength = 20;

    return plainText.length > maxLength
      ? plainText.substring(0, maxLength).trimEnd() + '...'
      : plainText;
  }

  /* ========================================================
     LOAD HOME COURSE CONTENT (including HSR when provisioned)
  ======================================================== */
  loadHomeCourseContent(): void {
    this.isLoading = true;

    this.adminService.getHomeCourseContent().subscribe({
      next: (response: HomeCourseContentModel[]) => {
        this.homeContent = response || [];
        this.isLoading = false;
      },
      error: error => {
        console.error('Failed to load home course content:', error);
        this.isLoading = false;
        this.showMessage('error', 'Failed to load home course content.');
      }
    });
  }

  /* Generic helpers work for ALL_COURSES, VOC, SKILL_SETS,
     HIGH_RISK, SHORT_COURSES, QUALIFICATIONS and HSR. */
  getContent(contentCode: string): HomeCourseContentModel | undefined {
    return this.homeContent.find(x => x.contentCode === contentCode);
  }

  getFieldValue(
    contentCode: string,
    field: 'title' | 'subTitle' | 'description'
  ): string {
    const record = this.getContent(contentCode);
    if (!record) {
      return '';
    }
    return record[field] || '';
  }

  /** An HSR edit replaces the corresponding circle text with its input. */
  isHsrEditing(): boolean {
    return this.editingKey?.startsWith(`${this.hsrContentCode}_`) ?? false;
  }

  isEditing(contentCode: string, field: string): boolean {
    return this.editingKey === `${contentCode}_${field}`;
  }

  startEdit(
    contentCode: string,
    field: 'title' | 'subTitle' | 'description'
  ): void {
    if (this.isSaving || this.editingKey) {
      return;
    }

    const record = this.getContent(contentCode);
    if (!record) {
      this.showMessage('error', `No home content record for ${contentCode}.`);
      return;
    }

    this.editingKey = `${contentCode}_${field}`;
    this.editValue = record[field] || '';
    this.message = '';
  }

  cancelEdit(): void {
    if (this.isSaving) {
      return;
    }
    this.editingKey = null;
    this.editValue = '';
  }

  /* HSR title, subtitle and description use this SAME save flow. */
  saveField(
    contentCode: string,
    field: 'title' | 'subTitle' | 'description'
  ): void {
    if (this.isSaving || !this.isEditing(contentCode, field)) {
      return;
    }

    const record = this.getContent(contentCode);
    if (!record) {
      this.showMessage('error', `No home content record for ${contentCode}.`);
      return;
    }

    const newValue = this.editValue.trim();
    if (field === 'title' && !newValue) {
      this.showMessage('error', 'Title cannot be empty.');
      return;
    }

    const payload: HomeCourseContentModel = {
      ...record,
      [field]: newValue || (field === 'title' ? '' : null)
    };

    this.isSaving = true;
    this.adminService.updateHomeCourseContent(payload).subscribe({
      next: (response: string) => {
        this.isSaving = false;

        if ((response || '').trim().toUpperCase() === 'SUCCESS') {
          this.homeContent = this.homeContent.map(item =>
            item.id === record.id ? payload : item
          );

          this.editingKey = null;
          this.editValue = '';
          this.showSavedFieldCheck(contentCode, field);
          this.message = '';
        } else {
          this.showMessage('error', 'Unable to update content.');
        }
      },
      error: error => {
        this.isSaving = false;
        console.error('Update failed:', error);
        this.showMessage('error', 'Failed to update content.');
      }
    });
  }

  showMessage(type: 'success' | 'error', message: string): void {
    this.messageType = type;
    this.message = message;
  }

  closeMessage(): void {
    this.message = '';
  }

  isRecentlySaved(contentCode: string, field: string): boolean {
    return this.savedFieldKey === `${contentCode}_${field}`;
  }

  showSavedFieldCheck(contentCode: string, field: string): void {
    if (this.savedFieldTimer) {
      clearTimeout(this.savedFieldTimer);
    }

    this.savedFieldKey = `${contentCode}_${field}`;
    this.savedFieldTimer = setTimeout(() => {
      this.savedFieldKey = null;
      this.savedFieldTimer = null;
    }, 1000);
  }
}
